// Portal-V2-Client — Concierge rebuild (2026-06-24).
//
// Mockup 3 chosen by Mark over Executive Hero / Editorial Statement on
// the basis that it feels like a live conversation with a broker rather
// than a static status board.
//
// Layout:
//   - Top nav: real horizontal Solara logo + entity switcher + people
//     drawer + logout.
//   - Greeting strip with subtitle summarising open deals + outstanding
//     docs.
//   - Two-column board:
//       LEFT  (1.65fr): per-application cards. Each card shows progress
//         + key chips + "Next from you" callout. Expanding a card
//         reveals the FULL checklist inline — no separate detail page,
//         no router push, no second screen. "Everything on one page"
//         per Mark.
//       RIGHT (1fr):
//         - MessagingPanel (broker card + thread + send form) for the
//           currently-active app — see components/client/MessagingPanel.jsx.
//         - Upcoming dates (settlement / valuations).
//         - Recent activity (placeholder for now — wires in next PR).
//   - Watermark: large solara-icon.png in the page background at ~3%
//     opacity. Never SVG-recreated; see feedback_never_recreate_solara_logo.
//
// What's intentionally REMOVED vs the old portal:
//   - Separate "detail view" subpage — collapsed into inline expansion.
//   - Lurid gradient progress bar (replaced with flat gold/green by stage).
//   - Interest rate chip (Mark's call — too sensitive/noisy for clients).
//   - "● online" presence indicator (was fictional — we have no real-time
//     pinging; replaced with "Replies within 1 business hour").
//
// What's preserved 1:1 from the old portal:
//   - All checklist actions: upload (direct-to-Drive), skip with reason,
//     add another row, remove single file, multi-file display per item.
//   - Entity switcher (Portals-4).
//   - People drawer (Portals-4).
//   - 30s polling for dashboard refresh + 30s polling for messages
//     (inside MessagingPanel).
//   - useNoIndex() so the portal stays out of Google.
//
// The /my-applications and detail-deeplink routes still work — when an
// appId is in the URL we just auto-expand that card on mount.

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import {
  AlertCircle,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  Clock,
  FileText,
  FolderOpen,
  Loader2,
  LogOut,
  Upload,
  Users,
} from "lucide-react";

import { uploadDirectToDrive, MAX_DIRECT_UPLOAD_BYTES } from "@/lib/uploadDirectToDrive";
import { openProtectedFile } from "@/lib/protectedFile";
import { useNoIndex } from "@/lib/useNoIndex";
import { useConfirmDialog } from "@/lib/useConfirmDialog";  // PR #95 / UX-1
import EntitySwitcher, {
  getActiveEntityId,
  setActiveEntityId,
} from "@/components/client/EntitySwitcher";
import PeopleDrawer from "@/components/client/PeopleDrawer";
import MessagingPanel from "@/components/client/MessagingPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Spine substage labels — keep this mapping in sync with backend's
// DEAL_STAGE_LABELS so the right-side chip reads naturally.
const STAGE_LABELS = {
  lead_new: "New",
  lead_application_in_progress: "Draft",
  lead_application_completed: "Submitted",
  assessment: "Assessment",
  submitted_to_lender: "Lender submitted",
  indicative_terms: "Indicative terms",
  conditions_precedent: "Conditions precedent",
  mortgage_docs_issued: "Mortgage docs",
  settlement: "Settlement",
  complete: "Complete",
  on_hold: "On hold",
  cancelled: "Cancelled",
  nonfin_sale_of_asset: "Sale of asset",
  nonfin_joint_venture: "Joint venture",
  nonfin_other: "In progress",
  draft: "Draft",
  submitted: "Submitted",
  under_review: "Under review",
  approved: "Approved",
  rejected: "Declined",
};

// The 5 visible pipeline segments for the segmented bar inside each card.
// stage-to-progress mapping — same conceptual ordering as the admin Kanban.
const STAGE_PROGRESS = {
  lead_new: 1,
  lead_application_in_progress: 1,
  lead_application_completed: 2,
  draft: 1,
  submitted: 2,
  under_review: 2,
  assessment: 2,
  submitted_to_lender: 3,
  indicative_terms: 3,
  conditions_precedent: 4,
  mortgage_docs_issued: 4,
  approved: 4,
  settlement: 5,
  complete: 5,
};

const STATUS_COLORS = {
  pending:      { bg: "bg-gray-100",  text: "text-gray-600",  label: "Pending" },
  uploaded:     { bg: "bg-blue-100",  text: "text-blue-600",  label: "Uploaded" },
  accepted:     { bg: "bg-green-100", text: "text-green-600", label: "Accepted" },
  rejected:     { bg: "bg-red-100",   text: "text-red-600",   label: "Reupload" },
  not_required: { bg: "bg-gray-100",  text: "text-gray-500",  label: "Not required" },
};

const STAGE_BADGE_COLORS = {
  lead_new:                    "bg-gray-100 text-gray-700",
  lead_application_in_progress:"bg-amber-100 text-amber-800",
  lead_application_completed:  "bg-blue-100 text-blue-800",
  assessment:                  "bg-blue-100 text-blue-800",
  submitted_to_lender:         "bg-amber-100 text-amber-800",
  indicative_terms:            "bg-amber-100 text-amber-800",
  conditions_precedent:        "bg-amber-100 text-amber-800",
  mortgage_docs_issued:        "bg-purple-100 text-purple-800",
  settlement:                  "bg-green-100 text-green-800",
  complete:                    "bg-green-100 text-green-800",
  on_hold:                     "bg-gray-200 text-gray-700",
  cancelled:                   "bg-red-100 text-red-700",
  nonfin_sale_of_asset:        "bg-indigo-100 text-indigo-700",
  nonfin_joint_venture:        "bg-indigo-100 text-indigo-700",
  nonfin_other:                "bg-indigo-100 text-indigo-700",
};

function fmtMoney(n) {
  if (n === null || n === undefined || n === "") return "";
  const num = Number(n);
  if (!Number.isFinite(num)) return "";
  return num.toLocaleString("en-AU", { style: "currency", currency: "AUD", maximumFractionDigits: 0 });
}

function fmtDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
  } catch { return ""; }
}

export default function ClientPortal() {
  useNoIndex();
  const { confirm, dialog } = useConfirmDialog();  // PR #95 / UX-1

  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  // Per-app detail cache: { [appId]: detailObject }
  const [appDetails, setAppDetails] = useState({});
  // Which app cards are currently expanded.
  const [expandedApps, setExpandedApps] = useState(new Set());
  // Per-category collapse state inside each expanded app:
  //   `${appId}:${categoryIndex}` -> bool
  const [expandedCats, setExpandedCats] = useState({});
  // APPLY-2 (2026-07-05) — was just the req id; now {id, pct} so the button
  // shows real progress instead of an indefinite spinner (HIGH-2: clients on
  // slow connections thought uploads had hung and gave up).
  const [uploading, setUploading] = useState(null);
  // Which app is "active" — drives the right-side MessagingPanel.
  const [activeAppId, setActiveAppId] = useState(null);

  // Entity switcher state (Portals-4).
  const [entities, setEntities] = useState([]);
  const [activeEntityId, setActiveEntityIdState] = useState(getActiveEntityId());
  const [peopleOpen, setPeopleOpen] = useState(false);

  const navigate = useNavigate();
  const { appId: appIdFromUrl } = useParams();

  // APPLY-2 (2026-07-05) — 401 session-rescue, mirroring the apply form's
  // HF-Apply-13 interceptor. The Portal-V2 rebuild didn't carry it over, so
  // an expired session mid-message/mid-upload just failed silently (HIGH-3).
  // On 401: one toast, bounce to /login with a return URL back to the
  // portal (magic-link users can request a fresh link from there).
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error?.response?.status === 401) {
          if (!window.__solara_portal_401_handled) {
            window.__solara_portal_401_handled = true;
            toast.info("Your session timed out — sign in again to keep going.");
            setTimeout(() => { window.__solara_portal_401_handled = false; }, 5000);
            navigate(`/login?return=${encodeURIComponent("/portal")}`);
          }
        }
        return Promise.reject(error);
      },
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, [navigate]);

  // -------------------- data loaders --------------------

  const loadDashboard = useCallback(async () => {
    try {
      let storedEntity = null;
      try { storedEntity = localStorage.getItem("solara.client.activeEntityId"); }
      catch (_) { /* localStorage may be blocked */ }
      const params = storedEntity ? { entity_id: storedEntity } : undefined;
      const res = await axios.get(`${API}/portal/dashboard`, params ? { params } : undefined);
      setDashboard(res.data);
      return res.data;
    } catch (e) {
      if (e?.response?.status === 401 || e?.response?.status === 403) navigate("/");
      return null;
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  const loadEntities = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/me/entities`);
      const rows = res.data?.entities || [];
      setEntities(rows);
      const stored = getActiveEntityId();
      const valid = rows.find((e) => e.id === stored);
      if (!valid && rows.length > 0) {
        setActiveEntityId(rows[0].id);
        setActiveEntityIdState(rows[0].id);
      } else if (valid) {
        setActiveEntityIdState(valid.id);
      } else {
        setActiveEntityId(null);
        setActiveEntityIdState(null);
      }
    } catch {
      /* not signed in or endpoint unreachable — leave empty */
    }
  }, []);

  const loadAppDetail = useCallback(async (appId) => {
    try {
      const res = await axios.get(`${API}/portal/applications/${appId}`);
      setAppDetails((prev) => ({ ...prev, [appId]: res.data }));
      return res.data;
    } catch (e) {
      toast.error("Could not load application detail");
      return null;
    }
  }, []);

  // Initial mount + 30s dashboard refresh.
  useEffect(() => {
    loadDashboard();
    loadEntities();
  }, [loadDashboard, loadEntities]);

  useEffect(() => {
    const t = setInterval(() => {
      // PERF-1 (2026-07-08) — skip the poll while the browser tab is hidden.
      if (document.hidden) return;
      loadDashboard();
      // Also refresh any expanded app's detail.
      expandedApps.forEach((appId) => { loadAppDetail(appId); });
    }, 30000);
    return () => clearInterval(t);
  }, [loadDashboard, loadAppDetail, expandedApps]);

  // Set default active app + auto-expand from URL when dashboard arrives.
  useEffect(() => {
    if (!dashboard) return;
    const apps = dashboard.applications || [];
    if (!apps.length) return;
    if (appIdFromUrl && apps.find((a) => a.id === appIdFromUrl)) {
      setActiveAppId(appIdFromUrl);
      handleExpand(appIdFromUrl, true);
    } else if (!activeAppId) {
      setActiveAppId(apps[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboard, appIdFromUrl]);

  // -------------------- expansion --------------------

  const handleExpand = useCallback(
    (appId, force = false) => {
      setExpandedApps((prev) => {
        const next = new Set(prev);
        if (next.has(appId) && !force) {
          next.delete(appId);
        } else {
          next.add(appId);
        }
        return next;
      });
      setActiveAppId(appId);
      if (!appDetails[appId]) {
        loadAppDetail(appId).then((detail) => {
          // Open all action-required categories by default.
          if (detail) {
            const next = { ...expandedCats };
            (detail.doc_categories || []).forEach((c, i) => {
              if (c.action_required > 0) next[`${appId}:${i}`] = true;
            });
            setExpandedCats(next);
          }
        });
      }
    },
    [appDetails, expandedCats, loadAppDetail]
  );

  const toggleCat = (appId, ci) => {
    const key = `${appId}:${ci}`;
    setExpandedCats((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // -------------------- checklist actions (unchanged from old portal) --------------------

  const handleUpload = (appId, reqId) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx,.csv,.txt";
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      if (file.size > MAX_DIRECT_UPLOAD_BYTES) {
        const mb = Math.round(MAX_DIRECT_UPLOAD_BYTES / (1024 * 1024));
        toast.error(`File too large (max ${mb}MB)`);
        return;
      }
      setUploading({ id: reqId, pct: 0 });
      try {
        await uploadDirectToDrive(file, {
          target_kind: "application_doc_request",
          application_id: appId,
          doc_request_id: reqId,
        }, {
          fileName: file.name,
          onProgress: (frac) => setUploading({ id: reqId, pct: Math.round((frac || 0) * 100) }),
        });
        toast.success(`${file.name} uploaded`);
        await loadAppDetail(appId);
        await loadDashboard();
      } catch (err) {
        // APPLY-2 — say WHY it failed, not just "failed". 401 is handled by
        // the session-rescue interceptor below; here we cover the rest.
        const detail = err?.response?.data?.detail || err?.message;
        const hint = !navigator.onLine
          ? "You appear to be offline — check your connection and try again."
          : detail || "Something went wrong — please try again, and contact us if it keeps happening.";
        toast.error(`Upload failed: ${hint}`, { duration: 8000 });
      } finally {
        setUploading(null);
      }
    };
    input.click();
  };

  const handleSkip = async (appId, reqId, itemName) => {
    const reason = window.prompt(
      `Mark "${itemName}" as not applicable?\n\nGive a brief reason (visible to your broker):`
    );
    if (reason === null) return;
    const trimmed = reason.trim();
    if (trimmed.length < 3) {
      toast.error("Please provide at least 3 characters.");
      return;
    }
    try {
      await axios.post(`${API}/applications/${appId}/doc-requests/${reqId}/skip`, { reason: trimmed });
      toast.success("Marked as not applicable");
      await loadAppDetail(appId);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not skip this item");
    }
  };

  const handleAddAnother = async (appId, reqId, itemName) => {
    if (!(await confirm({ title: `Add another "${itemName}" row?`, description: "This adds another row to your checklist.", confirmLabel: "Add row" }))) return;
    try {
      await axios.post(`${API}/applications/${appId}/doc-requests/${reqId}/duplicate`);
      toast.success("Added a new row — scroll down to upload it.");
      await loadAppDetail(appId);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add row");
    }
  };

  const handleRemoveFile = async (appId, reqId, fileId, fileName) => {
    if (!(await confirm({ title: `Remove "${fileName}"?`, description: "This removes the file from this checklist item.", confirmLabel: "Remove", destructive: true }))) return;
    try {
      await axios.delete(`${API}/applications/${appId}/doc-requests/${reqId}/files/${fileId}`);
      toast.success("File removed");
      await loadAppDetail(appId);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not remove file");
    }
  };

  const logout = () => {
    localStorage.removeItem("token");
    delete axios.defaults.headers.common["Authorization"];
    navigate("/");
  };

  // -------------------- derived data --------------------

  // WP-6 — memoised for the same reason as DealTeamSection: these minted a
  // fresh array/object every render, so every useMemo depending on them
  // recomputed every render.
  const apps = useMemo(() => dashboard?.applications || [], [dashboard]);
  const stats = useMemo(() => dashboard?.stats || {}, [dashboard]);
  const userName = (entities || []).find((e) => e.id === activeEntityId)?.entity_name || "";
  const activeApp = apps.find((a) => a.id === activeAppId) || apps[0];
  const hasEntities = (entities || []).length > 0;

  // Greeting subtitle — summarised at the top of the page.
  const subtitle = useMemo(() => {
    if (!apps.length) return "Welcome back — no active applications yet.";
    const totalDeals = apps.length;
    const totalAmount = apps.reduce((sum, a) => {
      const v = Number(a.loan_details?.net_loan_required || a.loan_details?.amount || 0);
      return sum + (Number.isFinite(v) ? v : 0);
    }, 0);
    const outstanding = stats.action_required || 0;
    const bits = [`${totalDeals} active application${totalDeals === 1 ? "" : "s"}`];
    if (totalAmount > 0) bits.push(`combined ${fmtMoney(totalAmount)}`);
    if (outstanding > 0) bits.push(`${outstanding} document${outstanding === 1 ? "" : "s"} need your action`);
    return bits.join(" · ");
  }, [apps, stats]);

  // Upcoming dates assembled from application metadata.
  const upcomingDates = useMemo(() => {
    const out = [];
    apps.forEach((a) => {
      const settlement = a.loan_details?.proposed_settlement_date || a.settlement_date;
      if (settlement) {
        out.push({
          appId: a.id,
          dateIso: settlement,
          title: "Settlement",
          sub: `${a.application_reference || ""} · ${fmtMoney(a.loan_details?.net_loan_required)}`.replace(/^·\s*/, ""),
        });
      }
    });
    return out
      .filter((d) => {
        const t = new Date(d.dateIso).getTime();
        return Number.isFinite(t) && t >= Date.now() - 86400000;
      })
      .sort((a, b) => new Date(a.dateIso) - new Date(b.dateIso))
      .slice(0, 5);
  }, [apps]);

  // -------------------- render --------------------

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f0ede4]">
        <div className="animate-spin w-8 h-8 border-3 border-[#bd9a59] border-t-transparent rounded-full" />
      </div>
    );
  }

  const activeEntity = (entities || []).find((e) => e.id === activeEntityId) || (entities || [])[0];

  const HeaderRight = (
    <div className="flex items-center gap-2">
      {hasEntities && (
        <EntitySwitcher
          entities={entities}
          activeEntityId={activeEntityId}
          onChange={(id) => {
            setActiveEntityIdState(id);
            loadDashboard();
          }}
          onCreated={(entity) => {
            setEntities((prev) => [...prev, entity]);
            setActiveEntityIdState(entity.id);
            loadDashboard();
          }}
        />
      )}
      {activeEntityId && (
        <button
          type="button"
          onClick={() => setPeopleOpen(true)}
          className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-white/80 hover:text-white hover:bg-white/10 rounded-md"
          title="People & access"
          data-testid="portal-people-button"
        >
          <Users className="w-4 h-4" />
          <span className="hidden sm:inline">People</span>
        </button>
      )}
      <Link
        to="/my-applications"
        className="hidden sm:flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-white/80 hover:text-white hover:bg-white/10 rounded-md"
        data-testid="portal-my-applications-link"
      >
        <FolderOpen className="w-4 h-4" />
        <span className="hidden md:inline">My applications</span>
      </Link>
      <button
        onClick={logout}
        className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-white/80 hover:text-white hover:bg-white/10 rounded-md"
        data-testid="portal-logout"
      >
        <LogOut className="w-4 h-4" />
        <span className="hidden md:inline">Sign out</span>
      </button>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#f0ede4] relative">
      {dialog}
      {/* Watermark — large spiral, very faint, fixed centre */}
      <div
        aria-hidden="true"
        className="pointer-events-none fixed inset-0 flex items-center justify-center opacity-[0.025]"
        style={{ zIndex: 0 }}
      >
        <img
          src="/logos/solara-icon.png"
          alt=""
          className="w-[720px] h-[720px] max-w-[80vw] max-h-[80vh] object-contain"
        />
      </div>

      {/* Top nav */}
      <header className="bg-[#021d49] text-white relative z-10">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
          {/* Nav lockup — solara-horizontal-white.png is the white-text
              variant of solara-vertical.png (file naming is inverted;
              vertical.png is the HORIZONTAL lockup). The original PNG has
              navy wordmark which is invisible on this navy nav; the
              -white variant has the wordmark recoloured to white while
              keeping the gold spiral icon. See reference_solara_logo_files
              and HF-Nav-Logo-White PR. */}
          <Link to="/portal" className="flex items-center gap-3 group" data-testid="portal-brand">
            <img
              src="/logos/solara-horizontal-white.png"
              alt="Solara Global Capital Partners"
              className="h-9 w-auto"
            />
          </Link>
          {HeaderRight}
        </div>
      </header>

      <PeopleDrawer
        entityId={activeEntityId}
        entityName={activeEntity?.entity_name}
        isOpen={peopleOpen}
        onClose={() => setPeopleOpen(false)}
      />

      {/* Greeting strip */}
      <section className="relative z-10 max-w-[1400px] mx-auto px-4 sm:px-6 pt-7 pb-2">
        <h1 className="font-serif text-2xl text-[#021d49]">
          {userName ? `Good day, ${userName}` : "Welcome back"}
        </h1>
        <p className="text-[13px] text-gray-600 mt-1">{subtitle}</p>
      </section>

      {/* KPI strip — only when at least one app */}
      {apps.length > 0 && (
        <section className="relative z-10 max-w-[1400px] mx-auto px-4 sm:px-6 pt-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KpiTile label="Applications" value={stats.total_applications ?? apps.length} />
            <KpiTile label="Action required" value={stats.action_required ?? 0} accent={(stats.action_required ?? 0) > 0} />
            <KpiTile label="Uploaded" value={stats.uploaded_documents ?? 0} />
            <KpiTile label="Accepted" value={stats.accepted_documents ?? 0} />
          </div>
        </section>
      )}

      {/* Main board */}
      <section className="relative z-10 max-w-[1400px] mx-auto px-4 sm:px-6 pt-4 pb-10 grid grid-cols-1 lg:grid-cols-[1.65fr_1fr] gap-5">
        {/* LEFT — applications */}
        <div>
          <h2 className="text-[12px] uppercase tracking-wider text-[#021d49] font-serif mb-3">
            Your applications
          </h2>
          {apps.length === 0 ? (
            <div className="bg-white border border-[#bd9a59]/30 rounded-xl p-10 text-center">
              <FileText className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 text-sm">No applications yet.</p>
              <Link
                to="/apply"
                className="inline-block mt-4 px-5 py-2 bg-[#021d49] text-white text-sm rounded-md hover:bg-[#052b66]"
                data-testid="portal-start-application"
              >
                Start an application
              </Link>
            </div>
          ) : (
            <div className="space-y-3">
              {apps.map((app) => {
                const isExpanded = expandedApps.has(app.id);
                const detail = appDetails[app.id];
                const stage = app.status || "lead_new";
                const stageLabel = STAGE_LABELS[stage] || (stage || "-").replace(/_/g, " ");
                const stageBadge = STAGE_BADGE_COLORS[stage] || "bg-gray-100 text-gray-700";
                const progress = STAGE_PROGRESS[stage] || 1;
                const amount = app.loan_details?.net_loan_required || app.loan_details?.amount;
                const loanType = app.loan_details?.loan_type || "";
                const term = app.loan_details?.term_months;
                const settlement = app.loan_details?.proposed_settlement_date;
                const docSummary = app.document_summary || {};
                const completedDocs = docSummary.accepted ?? 0;
                const totalDocs = docSummary.total ?? 0;
                const outstandingDocs = docSummary.action_required ?? 0;
                const company = app.company_borrower?.company_name || "Application";

                // First N pending docs to surface in "Next from you".
                const nextFromYou = (detail?.doc_categories || [])
                  .flatMap((c) => c.items || [])
                  .filter((it) => it.status === "pending" || it.status === "rejected")
                  .slice(0, 4);

                return (
                  <div
                    key={app.id}
                    className={`bg-white border rounded-xl overflow-hidden transition-colors ${
                      activeAppId === app.id
                        ? "border-[#bd9a59]"
                        : "border-[#bd9a59]/30"
                    }`}
                    data-testid={`portal-app-card-${app.id}`}
                  >
                    {/* Header row */}
                    <button
                      type="button"
                      onClick={() => handleExpand(app.id)}
                      className="w-full px-5 pt-4 pb-3 text-left hover:bg-[#fafaf9] transition-colors"
                      data-testid={`portal-app-toggle-${app.id}`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0 flex-1">
                          <div className="font-serif text-lg text-[#021d49] truncate">{company}</div>
                          <div className="text-[11px] font-mono text-gray-500 mt-0.5">
                            {app.application_reference || app.id?.slice(0, 8)}
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          {amount && (
                            <div className="font-serif text-xl text-[#021d49] leading-none">
                              {fmtMoney(amount)}
                            </div>
                          )}
                          <div className="text-[11px] text-gray-500 mt-1">
                            {loanType}{term ? ` · ${term} months` : ""}
                          </div>
                        </div>
                      </div>

                      {/* Segmented progress bar (5 cells, gold for current, green for done) */}
                      <div className="flex gap-1 mt-3">
                        {[1, 2, 3, 4, 5].map((seg) => (
                          <div
                            key={seg}
                            className={`flex-1 h-[5px] rounded-full ${
                              seg < progress
                                ? "bg-green-600"
                                : seg === progress
                                  ? "bg-[#bd9a59]"
                                  : "bg-[#e8e3d5]"
                            }`}
                          />
                        ))}
                      </div>

                      {/* Chips row */}
                      <div className="flex flex-wrap items-center gap-2 mt-3 text-[12px] text-gray-600">
                        <span className={`px-2 py-0.5 rounded-full font-medium ${stageBadge}`}>
                          {stageLabel}
                        </span>
                        {settlement && (
                          <span className="flex items-center gap-1">
                            <Clock className="w-3.5 h-3.5 text-[#bd9a59]" />
                            Settles {fmtDate(settlement)}
                          </span>
                        )}
                        {totalDocs > 0 && (
                          <span className="flex items-center gap-1">
                            <CheckCircle className="w-3.5 h-3.5 text-[#bd9a59]" />
                            {completedDocs} of {totalDocs} docs
                          </span>
                        )}
                        <span className="ml-auto flex items-center gap-1 text-[11px] text-gray-500">
                          {isExpanded ? "Hide checklist" : "Open checklist"}
                          {isExpanded ? (
                            <ChevronDown className="w-3.5 h-3.5" />
                          ) : (
                            <ChevronRight className="w-3.5 h-3.5" />
                          )}
                        </span>
                      </div>
                    </button>

                    {app.client_edit_unlocked_until && new Date(app.client_edit_unlocked_until) > new Date() && (
                      <div className="mx-5 mb-4 bg-[#fef9ed] border border-[#bd9a59]/40 rounded-md px-3 py-2.5 flex flex-wrap items-center justify-between gap-2" data-testid={`portal-finish-application-${app.id}`}>
                        <span className="text-[13px] text-[#021d49]">Your broker has asked you to finish this application.</span>
                        <Link
                          to={`/apply?resume=${encodeURIComponent(app.id)}`}
                          className="min-h-9 inline-flex items-center gap-1 px-3 bg-[#021d49] text-white text-[13px] rounded-md hover:bg-[#052b66]"
                        >
                          Finish application <ChevronRight className="w-3.5 h-3.5" />
                        </Link>
                      </div>
                    )}

                    {/* Next from you callout (always visible when collapsed AND something pending) */}
                    {!isExpanded && outstandingDocs > 0 && (
                      <div className="mx-5 mb-4 bg-[#fef9ed] border border-[#bd9a59]/40 rounded-md px-3 py-2.5">
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-[11px] uppercase tracking-wider text-[#021d49] font-medium">
                            Next from you
                          </span>
                          <span className="text-[11px] text-[#bd9a59]">
                            {outstandingDocs} outstanding
                          </span>
                        </div>
                        <div className="text-[13px] text-[#021d49] flex items-center justify-between gap-2">
                          <span className="truncate">
                            {nextFromYou.length > 0
                              ? nextFromYou.map((d) => d.document_name).slice(0, 2).join(" · ")
                                + (outstandingDocs > 2 ? ` · +${outstandingDocs - 2} more` : "")
                              : "Open checklist to see what's needed"}
                          </span>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleExpand(app.id, true);
                            }}
                            className="shrink-0 px-3 py-1 bg-[#021d49] text-white text-[11px] rounded-md hover:bg-[#052b66]"
                          >
                            Upload now
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Expanded checklist */}
                    {isExpanded && (
                      <div className="border-t border-[#f1f1ec] px-5 py-4 bg-[#fafaf9]">
                        {!detail ? (
                          <div className="flex items-center justify-center py-6">
                            <Loader2 className="w-4 h-4 text-[#bd9a59] animate-spin" />
                          </div>
                        ) : (
                          <ChecklistView
                            appId={app.id}
                            detail={detail}
                            expandedCats={expandedCats}
                            uploading={uploading}
                            onToggleCat={(ci) => toggleCat(app.id, ci)}
                            onUpload={(reqId) => handleUpload(app.id, reqId)}
                            onSkip={(reqId, name) => handleSkip(app.id, reqId, name)}
                            onAddAnother={(reqId, name) => handleAddAnother(app.id, reqId, name)}
                            onRemoveFile={(reqId, fileId, name) => handleRemoveFile(app.id, reqId, fileId, name)}
                          />
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* RIGHT — messaging + dates */}
        <aside className="space-y-3">
          {activeApp && (
            <MessagingPanel application={activeApp} />
          )}

          {upcomingDates.length > 0 && (
            <div className="bg-white border border-[#bd9a59]/30 rounded-xl p-4">
              <div className="text-[12px] uppercase tracking-wider text-[#021d49] font-medium mb-3">
                Upcoming dates
              </div>
              <div className="space-y-2">
                {upcomingDates.map((d, i) => (
                  <DateRow key={i} dateIso={d.dateIso} title={d.title} sub={d.sub} />
                ))}
              </div>
            </div>
          )}
        </aside>
      </section>
    </div>
  );
}

// ---------- subcomponents ----------

function KpiTile({ label, value, accent = false }) {
  return (
    <div className="bg-white border border-[#bd9a59]/30 rounded-lg px-4 py-3">
      <div className="text-[10px] uppercase tracking-wider text-gray-500">{label}</div>
      <div
        className={`font-serif text-2xl mt-1 leading-none ${accent ? "text-[#bd9a59]" : "text-[#021d49]"}`}
      >
        {value}
      </div>
    </div>
  );
}

function DateRow({ dateIso, title, sub }) {
  let day = "";
  let month = "";
  try {
    const d = new Date(dateIso);
    day = d.toLocaleDateString("en-AU", { day: "2-digit" });
    month = d.toLocaleDateString("en-AU", { month: "short" }).toUpperCase();
  } catch { /* */ }
  const daysAway = Math.max(0, Math.ceil((new Date(dateIso).getTime() - Date.now()) / 86400000));
  const isSoon = daysAway <= 7;
  return (
    <div className="flex items-center gap-3 py-1.5 border-t border-[#f1f1ec] first:border-t-0 first:pt-0">
      <div
        className={
          "w-11 h-11 rounded-md flex flex-col items-center justify-center shrink-0 " +
          (isSoon
            ? "border border-[#bd9a59] bg-[#fef9ed]"
            : "border border-gray-200 bg-white")
        }
      >
        <div className={`text-[9px] ${isSoon ? "text-[#bd9a59]" : "text-gray-500"}`}>{month}</div>
        <div className="font-serif text-base text-[#021d49] leading-none">{day}</div>
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[13px] text-[#021d49] truncate">{title}</div>
        <div className="text-[11px] text-gray-500 truncate">{sub}</div>
      </div>
      <div className={`text-[11px] shrink-0 ${isSoon ? "text-[#bd9a59]" : "text-gray-400"}`}>
        {daysAway === 0 ? "today" : daysAway === 1 ? "tomorrow" : `${daysAway}d`}
      </div>
    </div>
  );
}

function ChecklistView({ appId, detail, expandedCats, uploading, onToggleCat, onUpload, onSkip, onAddAnother, onRemoveFile }) {
  const overall = detail.progress?.percentage ?? 0;
  return (
    <div>
      {/* Mini progress header inside expansion */}
      <div className="flex items-center justify-between mb-3">
        <span className="text-[12px] text-[#021d49] font-medium">
          {detail.progress?.completed ?? 0} of {detail.progress?.total ?? 0} documents accepted
        </span>
        <span className="font-serif text-base text-[#bd9a59]">{overall}%</span>
      </div>

      <div className="space-y-2">
        {(detail.doc_categories || []).map((cat, ci) => {
          const key = `${appId}:${ci}`;
          const open = !!expandedCats[key];
          return (
            <div key={ci} className="bg-white border border-[#f1f1ec] rounded-md overflow-hidden">
              <button
                type="button"
                onClick={() => onToggleCat(ci)}
                className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-[#fafaf9]"
                data-testid={`portal-cat-${appId}-${ci}`}
              >
                <div className="flex items-center gap-2">
                  <FileText className="w-3.5 h-3.5 text-[#bd9a59]" />
                  <span className="text-[13px] font-medium text-[#021d49]">{cat.name}</span>
                  {cat.action_required > 0 && (
                    <span className="px-1.5 py-0.5 bg-red-100 text-red-600 text-[10px] rounded-full font-medium">
                      {cat.action_required} action
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 text-[11px] text-gray-500">
                  <span>{cat.completed}/{cat.total}</span>
                  {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                </div>
              </button>

              {open && (
                <div className="border-t border-[#f5f1e7] divide-y divide-[#f5f1e7]">
                  {(cat.items || []).map((item) => (
                    <ChecklistRow
                      key={item.id}
                      appId={appId}
                      item={item}
                      uploading={uploading}
                      onUpload={onUpload}
                      onSkip={onSkip}
                      onAddAnother={onAddAnother}
                      onRemoveFile={onRemoveFile}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {(!detail.doc_categories || detail.doc_categories.length === 0) && (
        <div className="text-center py-8 text-[12px] text-gray-400">
          No documents have been requested yet. Your broker will assign a checklist shortly.
        </div>
      )}
    </div>
  );
}

function ChecklistRow({ appId, item, uploading, onUpload, onSkip, onAddAnother, onRemoveFile }) {
  const st = STATUS_COLORS[item.status] || STATUS_COLORS.pending;
  const attached = item.attached_files || (item.file_name ? [{ id: null, file_name: item.file_name }] : []);
  const canUploadMore = item.status !== "not_required";

  return (
    <div className="px-4 py-2.5 space-y-1.5" data-testid={`portal-item-${item.id}`}>
      <div className="flex items-center gap-2">
        {item.status === "accepted" ? (
          <CheckCircle className="w-4 h-4 text-green-500 shrink-0" />
        ) : item.status === "rejected" ? (
          <AlertCircle className="w-4 h-4 text-red-500 shrink-0" />
        ) : item.status === "uploaded" ? (
          <Clock className="w-4 h-4 text-blue-500 shrink-0" />
        ) : item.status === "not_required" ? (
          <CheckCircle className="w-4 h-4 text-gray-400 shrink-0" />
        ) : (
          <div className="w-4 h-4 border-2 border-gray-300 rounded-full shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          <p className="text-[13px] text-gray-800">{item.document_name}</p>
          {attached.length === 0 && item.required === false && (
            <p className="text-[10px] text-slate-500 mt-0.5">Optional</p>
          )}
          {item.instructions_for_client && (
            <p className="text-[11px] text-gray-500 mt-0.5">{item.instructions_for_client}</p>
          )}
          {item.status === "rejected" && item.review_notes && (
            <p className="text-[11px] text-red-500 mt-0.5">Reason: {item.review_notes}</p>
          )}
          {item.duplicate_of && (
            <p className="text-[10px] text-gray-400 italic mt-0.5">Added by you</p>
          )}
        </div>
        <span className={`px-2 py-0.5 text-[10px] rounded-full ${st.bg} ${st.text}`}>{st.label}</span>
        {item.status === "not_required" && item.skip_reason && (
          <span
            className="hidden md:inline text-[10px] text-gray-400 italic truncate max-w-[180px]"
            title={`Reason: ${item.skip_reason}`}
          >
            Reason: {item.skip_reason}
          </span>
        )}
        {canUploadMore && (
          <button
            type="button"
            onClick={() => onUpload(item.id)}
            disabled={uploading?.id === item.id}
            className="flex items-center gap-1.5 px-2.5 py-1 text-[11px] bg-[#021d49] text-white rounded-md hover:bg-[#052b66] disabled:opacity-50"
            data-testid={`portal-upload-${item.id}`}
          >
            {uploading?.id === item.id ? (
              <>
                <Loader2 className="w-3 h-3 animate-spin" />
                {/* APPLY-2 — live percent so slow uploads don't look hung */}
                {`${uploading.pct}%`}
              </>
            ) : (
              <>
                <Upload className="w-3 h-3" />
                {attached.length > 0 ? "Add" : item.status === "rejected" ? "Re-upload" : "Upload"}
              </>
            )}
          </button>
        )}
        {(item.status === "pending" || item.status === "rejected") && (
          <button
            type="button"
            onClick={() => onSkip(item.id, item.document_name)}
            className="px-2 py-1 text-[11px] border border-gray-300 text-gray-600 rounded-md hover:bg-gray-50"
            title="Mark this item as not applicable"
            data-testid={`portal-skip-${item.id}`}
          >
            N/A
          </button>
        )}
        {item.status !== "not_required" && (
          <button
            type="button"
            onClick={() => onAddAnother(item.id, item.document_name)}
            className="px-2 py-1 text-[11px] border border-gray-300 text-gray-600 rounded-md hover:bg-gray-50"
            title="Add another row of the same type"
            data-testid={`portal-add-row-${item.id}`}
          >
            + Row
          </button>
        )}
      </div>

      {attached.length > 0 && (
        <div className="ml-6 space-y-1">
          {attached.map((f, i) => (
            <div
              key={f.id || `${item.id}-${i}`}
              className="flex items-center gap-2 text-[11px] bg-[#fafaf9] rounded px-2 py-1"
              data-testid={`portal-attached-${item.id}-${i}`}
            >
              <FileText className="w-3 h-3 text-gray-400 shrink-0" />
              <span className="flex-1 truncate text-gray-700">{f.file_name}</span>
              {f.id && (
                // Mirror admin DealChecklistTab (PR #133): bare <a href> to the
                // API omits the JWT Bearer header, so Preview always 401'd for
                // borrowers. openProtectedFile fetches with axios auth and
                // opens a blob URL. Pass file_id so multi-file rows open the
                // clicked attachment, not just the first match.
                <button
                  type="button"
                  onClick={() =>
                    openProtectedFile({
                      url: `${API}/applications/${appId}/documents/by-request/${item.id}?file_id=${encodeURIComponent(f.id)}`,
                      filename: f.file_name,
                      mode: "preview",
                    })
                  }
                  className="text-[10px] text-[#021d49] hover:underline"
                  data-testid={`portal-preview-${item.id}-${i}`}
                >
                  Preview
                </button>
              )}
              {f.id && item.status !== "accepted" && (
                <button
                  type="button"
                  onClick={() => onRemoveFile(item.id, f.id, f.file_name)}
                  className="text-[10px] text-red-500 hover:underline"
                  data-testid={`portal-remove-file-${f.id}`}
                >
                  Remove
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}